import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {
  AcceptEditorRequestDto,
  ArticleListDto,
  ChangeResourceStateDto,
  ChangeStateOfMissionDto,
  DeclineEditorRequestDto,
  FilterDto,
  MissionCompleteDto,
  MissionDto,
  MissionReportDto,
  MissionReportPreviewDto,
  MissionState,
  OpenEditorRequestOnContactListDto,
  Orders,
  QrTokenDto,
  ResourceDto, ResourceState, StartingMissionDto, StereotypeSimpleDto
} from "@server-models";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {map, tap} from "rxjs/operators";
import {IPaginationParams} from "@shared/interfaces/pagination-params.interface";
import {IRequestOptions} from "@shared/interfaces/request-options.interface";
import {CoreHttpClientApiBaseService} from "@core/services/http-client-api-base/core-http-client-api-base.service";
import {TechErrorHandlerResourceService} from "@tech/pages/inventory/services/tech-error-handler-resource.service";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";
import {CoreClockService} from "@core/services/core-clock.service";
import {TechErrorHandlerMissionService} from "@tech/pages/inventory/services/tech-error-handler-misison.service";

@Injectable({
  providedIn: 'root',
})
export class TechInventoryApiService extends CoreHttpClientApiBaseService {

  pagePostMissionPaginated(paramsPaginated: IPaginationParams,
                           body: FilterDto[]): Observable<ILocalPageOutputDto<MissionCompleteDto[]>> {

    let options: IRequestOptions = {
      params: {
        PageSize: paramsPaginated.pageSize.toString(),
        PageNumber: paramsPaginated.pageNumber.toString(),
        Cols: paramsPaginated.cols,
        Sort: paramsPaginated.sort?.toString() ?? Orders.Descending
      }
    }

    if (paramsPaginated?.sortField) {
      options = {
        ...options,
        params: {
          ...options.params,
          SortField: paramsPaginated.sortField.toString(),
        }
      }
    }

    return super.post<ILocalPageOutputDto<MissionCompleteDto[]>>(`/tech/mission/filter`,
      undefined, body, options).pipe(
      map((res: ILocalPageOutputDto<MissionCompleteDto[]>) => res)
    );
  }

  getMissionDetailById(id: string): Observable<MissionDto> {
    return super.get<MissionDto>(`/tech/mission/${ id }`, undefined, {
      errorHandler: new TechErrorHandlerMissionService(this._store, this._alertControllerService, this._translateService, this._router)
    });
  }

  postChangeMissionStateById(id: number, newState: MissionState, reason: string | null): Observable<void> {
    const body: ChangeStateOfMissionDto = {
      missionId: id,
      wantedState: newState,
      reason
    };
    return super.post<void>(`/tech/mission/${ id }/changeState`, undefined, body);
  }

  getMissionReportById(reportId: string): Observable<MissionReportDto> {
    return super.get<MissionReportDto>(`/tech/v2/missionreport/${ reportId }`, undefined);
  }

  postSubmitMissionReport(missionReport: MissionReportDto): Observable<MissionReportDto> {
    return super.post<MissionReportDto>(`/tech/v2/missionreport`, undefined, missionReport);
  }

  putSubmitMissionReportById(missionReport: MissionReportDto): Observable<MissionReportDto> {
    return super.put<MissionReportDto>(`/tech/v2/missionreport/${ missionReport.missionReportId }`, undefined, missionReport);
  }

  getMissionReportPreviewById(missionId: string): Observable<MissionReportPreviewDto> {
    return super.get<MissionReportPreviewDto>(`/tech/mission/${ missionId }/report/preview`, undefined);
  }

  replanMission(
    missionId: number,
    body: { actualStart: string, actualEnd: string }
  ): Observable<ChangeStateOfMissionDto> {
    return super.post(`/tech/mission/${ missionId }/replan`, undefined, body);
  }

  postMissionReportArticleFilter(body: any): Observable<ILocalPageOutputDto<ArticleListDto[]>> {
    const options: IRequestOptions = {
      params: {
        PageSize: '500',
        PageNumber: '1',
        Cols: 'Content',
        SortField: 'name',
        Sort: Orders.Descending.toString()
      }
    }
    return super.post<ILocalPageOutputDto<ArticleListDto[]>>(`/tech/article/filter`, undefined, body, options);
  }

  postMissionEditorQuestAccept(missionId: number, body: AcceptEditorRequestDto): Observable<void> {
    return super.post<void>(`/tech/mission/${ missionId }/editorquest/accept`, undefined, body)
  }

  postMissionEditorQuestDecline(missionId: number, body: DeclineEditorRequestDto): Observable<void> {
    return super.post<void>(`/tech/mission/${ missionId }/editorquest/decline`, undefined, body)
  }

  postMissionContactMeOpenEditorQuest(paramsPaginated: IPaginationParams,
                                      body: FilterDto[]): Observable<ILocalPageOutputDto<OpenEditorRequestOnContactListDto[]>> {
    const options: IRequestOptions = {
      params: {
        PageSize: paramsPaginated.pageSize.toString(),
        PageNumber: paramsPaginated.pageNumber.toString(),
        Cols: paramsPaginated.cols
      }
    }
    return super.post<ILocalPageOutputDto<OpenEditorRequestOnContactListDto[]>>(`/tech/contact/me/openeditorquest/filter`, undefined, body, options)
  }

  // resource
  getResourceDetailById(id: string): Observable<ResourceDto> {
    const options = {
      errorHandler: new TechErrorHandlerResourceService(this._store, this._alertControllerService, this._translateService, this._router)
    }
    return super.get<ResourceDto>(`/tech/resource/${ id }`, undefined, options);
  }

  postChangeResourceStateById(id: number, state: ResourceState): Observable<void> {
    const body: ChangeResourceStateDto = {
      state,
      isKeepingLocation: true,
    };

    return super.post<void>(`/tech/resource/${ id }/changeState`, undefined, body);
  }

  postChangeResourceLocationById(id: number, state: ResourceState, locationId: number): Observable<void> {
    const body: ChangeResourceStateDto = {
      state,
      locationId,
      isKeepingLocation: false,
    };

    return super.post<void>(`/tech/resource/${ id }/changeState`, undefined, body);
  }

  getResourceDetailByIdMissions(request: ISharedLocalBodyRequest,
                                id: number): Observable<ILocalPageOutputDto<MissionCompleteDto[]>> {
    const options: IRequestOptions = {
      params: {
        PageSize: request.pagination.pageSize?.toString(),
        PageNumber: request.pagination.pageNumber?.toString(),
        Cols: request.pagination.cols?.toString(),
        Sort: request.pagination.sort?.toString()!
      }
    }
    return super.get<ILocalPageOutputDto<MissionCompleteDto[]>>(`/tech/resource/${ id }/mission`, undefined, options);
  }

  getResourceIdByLinkKey(key: string): Observable<number> {
    return super.post<QrTokenDto>('/identity/login/loginquickaccess', true, { key }).pipe(map((token) => token?.resourceId ?? 0));
  }

  getArticleRestrictions(missionId: number, stereotypeId: number): Observable<StereotypeSimpleDto[]> {
    return super.get<MissionReportPreviewDto>(`/tech/mission/${ missionId }/report/preview?missionReportStereotypeId=${ stereotypeId }`).pipe(
      map(response => response.articleRestrictions ?? [])
    )
  }

  postMission(mission: StartingMissionDto): Observable<StartingMissionDto> {
    return super.post<StartingMissionDto>('/tech/mission/manual', true, mission);
  }

  postFollowUpMission(missionId: number, mission: StartingMissionDto): Observable<StartingMissionDto> {
    return super.post<StartingMissionDto>(`/tech/mission/${missionId}/followUp`, true, mission);
  }
}
